import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import useAuthState from '../hooks/useAuthState';
import Comment from './Comment';
import '../styles/Discussion.css';
import '../styles/Comment.css';
import { formatTimeCompact } from '../utils/formatTimeCompact';
import { getHeartIcon } from '../utils/getThemeIcon';
import { getAuthorIconUrl } from '../utils/getAuthorPlaceholder';
import { ThemeContext } from '../context/ThemeContext';
import { ApiContext } from '../context/ApiContext';
import apiCall from '../utils/apiCall';
import NewDiscussion from './NewDiscussion';

const Discussion = () => {
  const { id } = useParams();
  const [discussion, setDiscussion] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewComment, setShowNewComment] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState('');
  const [likedCommentIds, setLikedCommentIds] = useState([]);
  const [likedDiscussionIds, setLikedDiscussionIds] = useState([]);
  const [commentLikesTrigger, setCommentLikesTrigger] = useState(false);
  const [discussionLikesTrigger, setDiscussionLikesTrigger] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [expandedPosts, setExpandedPosts] = useState({});
  const [showCommentMenus, setShowCommentMenus] = useState({});
  const [showDiscussionMenu, setShowDiscussionMenu] = useState(false);
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const { getDiscussion, getCommentsByParent, createComment } = useAuth();
  const { isAuthenticated } = useAuthState();
  const apiUrl = useContext(ApiContext);
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDiscussion = async () => {
      try {
        const [discussionResponse, commentsResponse] = await Promise.all([
          getDiscussion(id),
          getCommentsByParent('discussion', id)
        ]);

        if (discussionResponse.ok) {
          const discussionData = await discussionResponse.json();
          setDiscussion(discussionData);
        } else {
          throw new Error('Failed to fetch discussion');
        }

        if (commentsResponse.ok) {
          const commentsData = await commentsResponse.json();
          setComments(commentsData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscussion();
  }, [id, commentLikesTrigger]);

  // Close comment menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close any open comment menus
      Object.keys(showCommentMenus).forEach(commentId => {
        if (showCommentMenus[commentId] && 
            event.target.closest(`.comment-hamburger-menu-${commentId}`) === null && 
            event.target.closest(`.comment-menu-dropdown`) === null) {
            setShowCommentMenus(prev => ({...prev, [commentId]: false}));
        }
      });
      
      // Close discussion menu if open
      if (showDiscussionMenu && 
          event.target.closest(`.open-discussion-hamburger-menu`) === null && 
          event.target.closest(`.open-discussion-menu-dropdown`) === null) {
          setShowDiscussionMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showCommentMenus, showDiscussionMenu]);

  useEffect(() => {
    const fetchLikedComments = async () => {
      if (!discussion || !discussion.id) {
        console.error('Discussion ID is not available for fetching liked comments');
        return; 
      }
      if (!isAuthenticated || comments.length === 0) {
        setLikedCommentIds([]);
        return;
      }
      const likedResponse = await apiCall(`${apiUrl}/api/v1/post/comment/liked`, 'POST', { postId: discussion.id, commentIds: comments.map((comment) => comment.id) });
      if (likedResponse.ok) {
        const likedData = await likedResponse.json();
        setLikedCommentIds(likedData.ids);
      } else {
        console.error('Failed to fetch liked comments');
      }
    }

    fetchLikedComments();
  }, [apiUrl, discussion, comments, isAuthenticated]);

  useEffect(() => {
    const fetchLikedDiscussion = async () => {
      if (!discussion || !discussion.id) {
        console.error('Discussion ID is not available for fetching liked discussion status');
        return; 
      }
      if (!isAuthenticated) {
        setLikedDiscussionIds([]);
        return;
      }
      // TODO: change to discussion/liked
      const likedResponse = await apiCall(`${apiUrl}/api/v1/post/liked`, 'POST', { ids: [discussion.id] });
      if (likedResponse.ok) {
        const likedData = await likedResponse.json();
        setLikedDiscussionIds(likedData.ids);
      } else {
        console.error('Failed to fetch liked discussion status');
      }
    }

    fetchLikedDiscussion();
  }, [apiUrl, discussion, isAuthenticated, discussionLikesTrigger]);

  const handleBack = () => {
    navigate(-1); // Go back to previous page (discussions list or post)
  };

  const handleLikeDiscussion = async (discussionId) => {
    try {
      const response = await apiCall(`${apiUrl}/api/v1/discussion/like`, 'POST', { id: discussionId });
      if (response.ok) {
        setDiscussionLikesTrigger(!discussionLikesTrigger);
      } else {
        console.error('Failed to like discussion');
      }
    } catch (error) {
      console.error('Error while liking discussion:', error);
    }
  };

  // Helper function to count lines in post content
  const countPostLines = (text) => {
    if (!text) return 0;
    return text.split('\n').length;
  };

  // Helper function to count lines in comment content
  const countLines = (text) => {
    if (!text) return 0;
    return text.split('\n').length;
  };

  // Helper function to toggle post expansion
  const togglePostExpansion = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  // Helper function to toggle comment expansion
  const toggleCommentExpansion = (commentId) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const handleCommentAdded = async () => {
    // Refresh comments after new comment is added
    const commentsResponse = await getCommentsByParent('discussion', id);
    if (commentsResponse.ok) {
      const updatedComments = await commentsResponse.json();
      setComments(updatedComments);
    }
    setShowNewComment(false);
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await apiCall(`${apiUrl}/api/v1/post/comment/like`, 'POST', { postId: id, commentId: commentId });
      if (response.ok) {
        setCommentLikesTrigger(!commentLikesTrigger);
      } else {
        console.error('Failed to like comment');
      }
    } catch (error) {
      console.error('Error while liking comment:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newCommentContent.trim()) return;
    
    try {
      // Map frontend data to backend expected format
      const commentData = {
        content: newCommentContent,
        parentId: id,
        parentType: 'discussion'
      };

      const response = await createComment(commentData);

      if (response.ok) {
        setNewCommentContent('');
        // Refresh comments to show the new one
        const commentsResponse = await getCommentsByParent('discussion', id);
        if (commentsResponse.ok) {
          const data = await commentsResponse.json();
          setComments(data);
        }
      } else {
        console.error('Failed to add comment');
      }
    } catch (error) {
      console.error('Error while adding comment:', error);
    }
  };

  if (loading) return <div className="open-discussion-loading">Loading discussion...</div>;
  if (error) return <div className="open-discussion-error">{error}</div>;
  if (!discussion) return <div className="open-discussion-not-found">Discussion not found</div>;

  return (
    <div className="open-discussion-container">
      <div className="open-discussion-content-wrapper">
        <div className="open-discussion-item">
          <img src={getAuthorIconUrl(discussion, apiUrl, theme)} alt="Author" className="open-discussion-author-icon" />
          <div className="open-discussion-body">
            <div className="open-discussion-header">
              <p className="open-discussion-author">@{discussion.authorUsername}</p>
              <p className="open-discussion-date">{formatTimeCompact(discussion.createdAt)}</p>
            </div>
            <div className="open-discussion-content-container">
          {countPostLines(discussion.content) > 10 && !expandedPosts[discussion.id] ? (
            <>
              <p className="open-discussion-content open-discussion-content-collapsed">
                {discussion.content.split('\n').slice(0, 10).join('\n')}
              </p>
              <div className="open-discussion-expand-button-container">
                <button
                  className="open-discussion-expand-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePostExpansion(discussion.id);
                  }}
                >
                  ...show more
                </button>
              </div>
            </>
          ) : (
            <p className="open-discussion-content">
              {discussion.content}
            </p>
          )}
          {countPostLines(discussion.content) > 10 && expandedPosts[discussion.id] && (
            <div className="open-discussion-expand-button-container">
              <button
                className="open-discussion-expand-button"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePostExpansion(discussion.id);
                }}
              >
                show less
              </button>
            </div>
          )}
            </div>
            <div className="open-discussion-actions-container">
          <div className="open-discussion-like-action">
            <img
              src={getHeartIcon(theme, likedDiscussionIds.includes(discussion.id))}
              alt="Like"
              className="open-discussion-like-icon"
              onClick={(e) => {
                e.stopPropagation();
                handleLikeDiscussion(discussion.id);
              }}
            />
            <span>{discussion.likesCount || 0}</span>
          </div>
          <div className="open-discussion-hamburger-menu">
            <div
              className="open-discussion-menu-icon"
              onClick={(e) => {
                e.stopPropagation();
                setShowDiscussionMenu(!showDiscussionMenu);
              }}
              aria-label="Discussion options menu"
              role="button"
              tabIndex="0"
            >
              <div className="open-discussion-three-dots-icon"></div>
            </div>
            {showDiscussionMenu && (
              <div className="open-discussion-menu-dropdown">
                <div
                  className="open-discussion-menu-item-disabled"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNewDiscussion(true);
                    setShowDiscussionMenu(false);
                  }}
                >
                  Start Discussion (coming soon)
                </div>
              </div>
            )}
          </div>
        </div>
          </div>

        </div>
        <div className="open-discussion-comments-container">
          <ul>
            {comments.map((comment) => (
              <li 
                key={comment.id} 
                className="comment-item"
              >
                <img src={getAuthorIconUrl(comment, apiUrl, theme)} alt="Author" className="comment-author-icon" />
                <div className="comment-body">
                  <div className="comment-header">
                    <span className="comment-author">@{comment.authorUsername}</span>
                    <span className="comment-date">{formatTimeCompact(comment.createdAt)}</span>
                  </div>
                  <div className="comment-content-container">
                    {countLines(comment.content) > 5 && !expandedComments[comment.id] ? (
                      <>
                        <p className="open-comment-content open-comment-content-collapsed">
                          {comment.content.split('\n').slice(0, 5).join('\n')}
                        </p>
                        <div className="comment-expand-button-container">
                          <button
                            className="comment-expand-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCommentExpansion(comment.id);
                            }}
                          >
                            ...show more
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="open-comment-content">
                        {comment.content}
                      </p>
                    )}
                    {expandedComments[comment.id] && countLines(comment.content) > 5 && (
                      <div className="comment-expand-button-container">
                        <button
                          className="comment-expand-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCommentExpansion(comment.id);
                          }}
                        >
                          show less
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="comment-actions-container">
                    <div className="comment-like-action">
                      <img
                        src={getHeartIcon(theme, likedCommentIds.includes(comment.id))}
                        alt="Like"
                        className="comment-like-icon"
                        onClick={(e) => {
                          e.stopPropagation(); 
                          handleLikeComment(comment.id);
                        }}
                      />
                      <span>{ comment.likesCount }</span>
                    </div>
                    <div className={`comment-hamburger-menu comment-hamburger-menu-${comment.id}`}>
                      <div
                        className="open-discussion-menu-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCommentMenus(prev => ({...prev, [comment.id]: !prev[comment.id]}));
                        }}
                        aria-label="Comment options menu"
                        role="button"
                        tabIndex="0"
                      >
                        <div className="open-discussion-three-dots-icon"></div>
                      </div>
                      {showCommentMenus[comment.id] && (
                        <>
                          <div className="comment-menu-dropdown">
                            <div
                              className="open-comment-menu-item-disabled"
                              style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                              Reply (coming soon)
                            </div>
                            <div
                              className="open-comment-menu-item-disabled"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCommentMenus(prev => ({...prev, [comment.id]: false}));
                              }}
                            >
                              Start Discussion (coming soon)
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="open-discussion-comment-input-area">
        <div className="open-discussion-comment-input-container">
          <textarea
            ref={(textarea) => {
              if (textarea) {
                // Auto-resize textarea to fit content
                textarea.style.height = 'auto';
                textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 40), 200)}px`;
              }
            }}
            value={newCommentContent}
            onChange={(e) => {
              setNewCommentContent(e.target.value);
              // Auto-resize on content change
              const textarea = e.target;
              textarea.style.height = 'auto';
              textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 40), 200)}px`;
            }}
            placeholder="Add a comment..."
            className="open-discussion-comment-input"
            rows="1"
            onKeyDown={(e) => {
              // Allow Enter to add new lines (default behavior)
              // Only submit on Ctrl+Enter or Cmd+Enter
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleAddComment();
              }
            }}
          />
          <button
            className="open-discussion-comment-reply-button"
            onClick={handleAddComment}
            disabled={!newCommentContent.trim()}
          >
            Reply
          </button>
        </div>
      </div>
    </div>
  );
};

export default Discussion;
